/**
 * Forecast month column keys: API spreads, table, and CSV import.
 *
 * Canonical header: "April 2026" (full month name + 4-digit year).
 * Parsing uses date-fns strict format tokens (already a project dependency).
 */

import { format, isValid, parse } from "date-fns";

/** Used when parsing 2-digit years (yy); century follows date-fns rules. */
const REF_FOR_TWO_DIGIT_YEAR = new Date(2000, 0, 1);

/** Try in order: full month first, then common Excel export shapes. */
const CSV_PERIOD_PARSE_FORMATS = ["MMMM yyyy", "MMM-yy", "yy-MMM"];

function tryParseForecastPeriodHeader(trimmed) {
  if (!trimmed) return null;
  for (const pattern of CSV_PERIOD_PARSE_FORMATS) {
    const d = parse(trimmed, pattern, REF_FOR_TWO_DIGIT_YEAR);
    if (isValid(d)) return d;
  }
  return null;
}

export const FORECAST_MONTH_COL_REGEX = /^[A-Za-z]+ \d{4}$/;

/** True if the column key is a calendar month used for forecast spreads (canonical API shape). */
export function isForecastMonthColumn(header) {
  return FORECAST_MONTH_COL_REGEX.test(String(header ?? "").trim());
}

/**
 * Map a CSV column title to the canonical month key, or return the trimmed string unchanged.
 */
export function canonicalMonthColumnFromCsvHeader(csvCol) {
  const trimmed = (csvCol || "").trim();
  if (!trimmed) return trimmed;
  const d = tryParseForecastPeriodHeader(trimmed);
  if (d) return format(d, "MMMM yyyy");
  return trimmed;
}

/** True if the string parses as a month-period column for CSV allowlists. */
export function isForecastCsvPeriodColumn(s) {
  return tryParseForecastPeriodHeader((s || "").trim()) != null;
}
